'use client'

/* eslint-disable max-lines-per-function, complexity */
// Alert component requires comprehensive state management and conditional rendering

import { forwardRef, useState, useCallback, useEffect, ReactNode } from 'react'
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react'
import { safeAccess, safeArrayAccess } from '../utils/security'

/**
 * BIRHAUS Alert Component
 * 
 * Enhanced alert for inline feedback implementing BIRHAUS principles:
 * - Spanish-first messages and labels
 * - Progressive disclosure for detailed information
 * - Dismissible with memory (localStorage)
 * - Max 2 action buttons (cognitive load)
 * - Accessibility compliant (WCAG AA+)
 * - Icon support for visual clarity
 * - Auto-hide for temporary alerts
 * - Undo functionality for destructive actions
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export type AlertVariant = 'success' | 'error' | 'warning' | 'info'

export interface BirhausAlertAction {
  label: string
  labelEs?: string
  labelEn?: string
  onClick: () => void | Promise<void>
  variant?: 'primary' | 'secondary'
  loading?: boolean
}

export interface BirhausAlertProps {
  // BIRHAUS Principle #7: Bilingual by design
  title?: string
  titleEs?: string
  titleEn?: string
  message?: string
  messageEs?: string
  messageEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  details?: string
  detailsEs?: string
  detailsEn?: string
  collapsible?: boolean
  defaultExpanded?: boolean
  
  // Appearance and behavior
  variant?: AlertVariant
  size?: 'sm' | 'md' | 'lg'
  
  // Dismissal
  dismissible?: boolean
  dismissKey?: string // For localStorage persistence
  onDismiss?: () => void
  autoHide?: boolean
  autoHideDelay?: number
  
  // BIRHAUS Principle #3: Max 2 actions (cognitive load)
  actions?: BirhausAlertAction[]
  
  // Icons
  showIcon?: boolean
  customIcon?: ReactNode
  
  // BIRHAUS Principle #6: Accessibility = dignity
  role?: 'alert' | 'alertdialog' | 'status'
  
  // Locale
  locale?: 'es' | 'en'
  
  // Standard props
  className?: string
  children?: ReactNode
}

export const BirhausAlert = forwardRef<HTMLDivElement, BirhausAlertProps>(({
  // Content (Spanish-first)
  title,
  titleEs,
  titleEn,
  message,
  messageEs,
  messageEn,
  
  // Progressive disclosure
  details,
  detailsEs,
  detailsEn,
  collapsible = false,
  defaultExpanded = false,
  
  // Appearance
  variant = 'info',
  size = 'md',
  
  // Dismissal
  dismissible = false,
  dismissKey,
  onDismiss,
  autoHide = false,
  autoHideDelay = 5000,
  
  // Actions (BIRHAUS: Max 2)
  actions = [],
  
  // Icons
  showIcon = true,
  customIcon,
  
  // Accessibility
  role = 'alert',
  
  // Locale
  locale = 'es',
  
  // Standard props
  className,
  children,
  ...props
}, ref) => {
  // State management
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [isDismissed, setIsDismissed] = useState(false)
  const [actionLoading, setActionLoading] = useState<{ [key: number]: boolean }>({})
  
  // Check if alert was previously dismissed
  const wasPreviouslyDismissed = dismissKey ? 
    typeof window !== 'undefined' && localStorage.getItem(`birhaus-alert-dismissed-${dismissKey}`) === 'true' : 
    false
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback?: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback || ''
    }
    return primary || english || spanish || fallback || ''
  }, [locale])
  
  const displayTitle = getDisplayText(title, titleEs, titleEn)
  const displayMessage = getDisplayText(message, messageEs, messageEn)
  const displayDetails = getDisplayText(details, detailsEs, detailsEn)
  
  // Handle dismissal
  const handleDismiss = useCallback(() => {
    setIsDismissed(true)
    
    // Remember dismissal in localStorage
    if (dismissKey && typeof window !== 'undefined') {
      localStorage.setItem(`birhaus-alert-dismissed-${dismissKey}`, 'true')
    }
    
    onDismiss?.()
  }, [dismissKey, onDismiss])
  
  // Handle action clicks
  const handleActionClick = useCallback(async (action: BirhausAlertAction, index: number) => {
    setActionLoading(prev => ({ ...prev, [index]: true }))
    
    try {
      await action.onClick()
    } catch (error) {
      console.error('Alert action error:', error)
    } finally {
      setActionLoading(prev => ({ ...prev, [index]: false }))
    }
  }, [])
  
  // Handle expansion toggle
  const handleToggleExpansion = useCallback(() => {
    setIsExpanded(prev => !prev)
  }, [])
  
  // Auto-hide functionality
  useEffect(() => {
    if (autoHide && !isDismissed) {
      const timer = setTimeout(() => {
        handleDismiss()
      }, autoHideDelay)
      
      return () => clearTimeout(timer)
    }
  }, [autoHide, autoHideDelay, isDismissed, handleDismiss])
  
  // Don't render if dismissed (either now or previously)
  if (isDismissed || wasPreviouslyDismissed) {
    return null
  }
  
  // Variant configurations
  const variantConfig = {
    success: {
      bg: 'bg-green-50 border-green-200',
      icon: CheckCircle2,
      iconColor: 'text-green-500',
      titleColor: 'text-green-800',
      messageColor: 'text-green-700',
      actionPrimary: 'bg-green-600 hover:bg-green-700 text-white',
      actionSecondary: 'text-green-700 hover:text-green-800 border-green-300'
    },
    error: {
      bg: 'bg-red-50 border-red-200',
      icon: AlertCircle,
      iconColor: 'text-red-500',
      titleColor: 'text-red-800',
      messageColor: 'text-red-700',
      actionPrimary: 'bg-red-600 hover:bg-red-700 text-white',
      actionSecondary: 'text-red-700 hover:text-red-800 border-red-300'
    },
    warning: {
      bg: 'bg-yellow-50 border-yellow-200',
      icon: AlertTriangle,
      iconColor: 'text-yellow-500',
      titleColor: 'text-yellow-800',
      messageColor: 'text-yellow-700',
      actionPrimary: 'bg-yellow-600 hover:bg-yellow-700 text-white',
      actionSecondary: 'text-yellow-700 hover:text-yellow-800 border-yellow-300'
    },
    info: {
      bg: 'bg-blue-50 border-blue-200',
      icon: Info,
      iconColor: 'text-blue-500',
      titleColor: 'text-blue-800',
      messageColor: 'text-blue-700',
      actionPrimary: 'bg-blue-600 hover:bg-blue-700 text-white',
      actionSecondary: 'text-blue-700 hover:text-blue-800 border-blue-300'
    }
  }
  
  // Ensure variant is a valid key to prevent object injection
  const validVariant = variant in variantConfig ? variant : 'info'
  const config = safeAccess(variantConfig, validVariant, variantConfig.info)
  const IconComponent = customIcon ? () => customIcon : config.icon
  
  // Size classes
  const sizeClasses = {
    sm: 'p-3 text-sm',
    md: 'p-4 text-base',
    lg: 'p-5 text-lg'
  }
  
  // Ensure size is a valid key to prevent object injection
  const validSize = size in sizeClasses ? size : 'md'
  
  // BIRHAUS: Limit to max 2 actions for cognitive load
  const limitedActions = actions.slice(0, 2)
  
  return (
    <div
      ref={ref}
      role={role}
      aria-live={role === 'alert' ? 'polite' : undefined}
      className={cn(
        'birhaus-alert border rounded-lg transition-all duration-200',
        config.bg,
        safeAccess(sizeClasses, validSize, sizeClasses.md),
        className
      )}
      {...props}
    >
      <div className="flex items-start space-x-3">
        {/* Icon */}
        {showIcon && (
          <div className="flex-shrink-0">
            <IconComponent className={cn('h-5 w-5', config.iconColor)} />
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Title */}
              {displayTitle && (
                <h3 className={cn('font-medium', config.titleColor)}>
                  {displayTitle}
                </h3>
              )}
              
              {/* Message */}
              {displayMessage && (
                <p className={cn(
                  'mt-1',
                  config.messageColor,
                  !displayTitle && 'font-medium'
                )}>
                  {displayMessage}
                </p>
              )}
              
              {/* Custom content */}
              {children && (
                <div className={cn('mt-2', config.messageColor)}>
                  {children}
                </div>
              )}
            </div>
            
            {/* Dismiss button */}
            {dismissible && (
              <button
                type="button"
                onClick={handleDismiss}
                className="flex-shrink-0 ml-3 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Cerrar alerta"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* Collapsible details */}
          {collapsible && displayDetails && (
            <div className="mt-2">
              <button
                type="button"
                onClick={handleToggleExpansion}
                className={cn(
                  'flex items-center space-x-1 text-sm font-medium transition-colors',
                  config.titleColor,
                  'hover:opacity-80'
                )}
                aria-expanded={isExpanded}
                aria-controls={`details-${dismissKey || 'default'}`}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                <span>{isExpanded ? 'Ocultar detalles' : 'Ver detalles'}</span>
              </button>
              
              {isExpanded && (
                <div
                  id={`details-${dismissKey || 'default'}`}
                  className={cn('mt-2 text-sm', config.messageColor)}
                >
                  {displayDetails}
                </div>
              )}
            </div>
          )}
          
          {/* Actions */}
          {limitedActions.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {limitedActions.map((action, index) => {
                const displayActionLabel = getDisplayText(
                  action.label,
                  action.labelEs,
                  action.labelEn,
                  action.label
                )
                // Safe array access with bounds checking
                const isLoading = Array.isArray(actionLoading) 
                  ? safeArrayAccess(actionLoading, index, false)
                  : false
                const isPrimary = action.variant === 'primary' || index === 0
                
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleActionClick(action, index)}
                    disabled={isLoading || action.loading}
                    className={cn(
                      'inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md',
                      'transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
                      isPrimary ? config.actionPrimary : cn(
                        'border bg-transparent',
                        config.actionSecondary
                      )
                    )}
                  >
                    {(isLoading || action.loading) && (
                      <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full mr-2" />
                    )}
                    {displayActionLabel}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

BirhausAlert.displayName = 'BirhausAlert'

// Hook for managing alert state
export const useBirhausAlert = (dismissKey?: string) => {
  const [isVisible, setIsVisible] = useState(true)
  
  const dismiss = useCallback(() => {
    setIsVisible(false)
    
    if (dismissKey && typeof window !== 'undefined') {
      localStorage.setItem(`birhaus-alert-dismissed-${dismissKey}`, 'true')
    }
  }, [dismissKey])
  
  const reset = useCallback(() => {
    setIsVisible(true)
    
    if (dismissKey && typeof window !== 'undefined') {
      localStorage.removeItem(`birhaus-alert-dismissed-${dismissKey}`)
    }
  }, [dismissKey])
  
  const wasDismissed = dismissKey && typeof window !== 'undefined' ? 
    localStorage.getItem(`birhaus-alert-dismissed-${dismissKey}`) === 'true' : 
    false
  
  return {
    isVisible: isVisible && !wasDismissed,
    dismiss,
    reset,
    wasDismissed
  }
}

// Common alert configurations for IPU Admin
export const birhausAlertPresets = {
  // Success alerts
  memberSaved: {
    variant: 'success' as const,
    titleEs: 'Miembro Guardado',
    titleEn: 'Member Saved',
    messageEs: 'La información del miembro se guardó exitosamente.',
    messageEn: 'Member information was saved successfully.'
  },
  
  donationRecorded: {
    variant: 'success' as const,
    titleEs: 'Donación Registrada',
    titleEn: 'Donation Recorded',
    messageEs: 'La donación se registró exitosamente en el sistema.',
    messageEn: 'The donation was successfully recorded in the system.'
  },
  
  paymentProcessed: {
    variant: 'success' as const,
    titleEs: 'Pago Procesado',
    titleEn: 'Payment Processed',
    messageEs: 'El pago se procesó exitosamente.',
    messageEn: 'The payment was processed successfully.'
  },
  
  // Error alerts
  saveError: {
    variant: 'error' as const,
    titleEs: 'Error al Guardar',
    titleEn: 'Save Error',
    messageEs: 'No se pudo guardar la información. Por favor intenta nuevamente.',
    messageEn: 'Could not save information. Please try again.',
    dismissible: true
  },
  
  validationError: {
    variant: 'error' as const,
    titleEs: 'Errores en el Formulario',
    titleEn: 'Form Errors',
    messageEs: 'Por favor corrige los errores marcados en el formulario.',
    messageEn: 'Please fix the errors marked in the form.',
    dismissible: true
  },
  
  networkError: {
    variant: 'error' as const,
    titleEs: 'Error de Conexión',
    titleEn: 'Connection Error',
    messageEs: 'No se pudo conectar con el servidor. Verifica tu conexión a internet.',
    messageEn: 'Could not connect to server. Check your internet connection.',
    dismissible: true
  },
  
  // Warning alerts
  unsavedChanges: {
    variant: 'warning' as const,
    titleEs: 'Cambios No Guardados',
    titleEn: 'Unsaved Changes',
    messageEs: 'Tienes cambios sin guardar que se perderán si sales de esta página.',
    messageEn: 'You have unsaved changes that will be lost if you leave this page.',
    actions: [
      {
        label: 'Guardar',
        labelEs: 'Guardar',
        labelEn: 'Save',
        variant: 'primary' as const,
        onClick: () => {}
      },
      {
        label: 'Descartar',
        labelEs: 'Descartar',
        labelEn: 'Discard',
        variant: 'secondary' as const,
        onClick: () => {}
      }
    ]
  },
  
  deleteConfirmation: {
    variant: 'warning' as const,
    titleEs: '¿Eliminar elemento?',
    titleEn: 'Delete item?',
    messageEs: 'Esta acción no se puede deshacer.',
    messageEn: 'This action cannot be undone.',
    actions: [
      {
        label: 'Eliminar',
        labelEs: 'Eliminar',
        labelEn: 'Delete',
        variant: 'primary' as const,
        onClick: () => {}
      },
      {
        label: 'Cancelar',
        labelEs: 'Cancelar',
        labelEn: 'Cancel',
        variant: 'secondary' as const,
        onClick: () => {}
      }
    ]
  },
  
  // Info alerts
  backupReminder: {
    variant: 'info' as const,
    titleEs: 'Recordatorio de Respaldo',
    titleEn: 'Backup Reminder',
    messageEs: 'Se recomienda hacer un respaldo de los datos semanalmente.',
    messageEn: 'It is recommended to backup data weekly.',
    detailsEs: 'Ve a Configuración > Respaldos para crear un respaldo completo de todos los datos de la iglesia.',
    detailsEn: 'Go to Settings > Backups to create a complete backup of all church data.',
    collapsible: true,
    dismissible: true,
    dismissKey: 'backup-reminder-weekly'
  },
  
  updateAvailable: {
    variant: 'info' as const,
    titleEs: 'Actualización Disponible',
    titleEn: 'Update Available',
    messageEs: 'Hay una nueva versión de IPU Admin disponible.',
    messageEn: 'A new version of IPU Admin is available.',
    actions: [
      {
        label: 'Actualizar',
        labelEs: 'Actualizar',
        labelEn: 'Update',
        variant: 'primary' as const,
        onClick: () => {}
      },
      {
        label: 'Después',
        labelEs: 'Después',
        labelEn: 'Later',
        variant: 'secondary' as const,
        onClick: () => {}
      }
    ]
  }
}